import { cn } from '@/lib/cn';
import {
  type DayRow,
  type MetricDef,
  formatNum,
} from '@/lib/miniprogramAnalyticsShared';
import { AnalyticsDeltaBadge } from '@/components/miniprogramAnalytics/AnalyticsDeltaBadge';

type Props = {
  daily: DayRow[];
  metric: MetricDef;
  barAreaPx: number;
  compact?: boolean;
};

export function AnalyticsTrendChart({ daily, metric, barAreaPx, compact }: Props) {
  const values = daily.map((d) => Math.max(0, Number(d[metric.key]) || 0));
  const max = Math.max(1, ...values);
  const last = daily[daily.length - 1];
  const prev = daily.length > 1 ? daily[daily.length - 2] : null;

  return (
    <div
      className={cn(
        'rounded-2xl border border-slate-200 bg-white shadow-sm',
        compact ? 'p-3' : 'p-4'
      )}
    >
      <div className={cn('flex flex-wrap items-end justify-between gap-2', compact ? 'mb-3' : 'mb-4')}>
        <div className="min-w-0">
          <p className={cn('font-semibold text-slate-900', compact ? 'text-sm' : 'text-sm')}>
            {metric.label}趋势
          </p>
          {!compact ? (
            <p className="mt-0.5 text-xs text-slate-500">每日 {metric.short} 柱状对比</p>
          ) : null}
        </div>
        {last ? (
          <div className="text-right">
            <p className={cn('font-bold tabular-nums text-slate-900', compact ? 'text-lg' : 'text-2xl')}>
              {formatNum(Number(last[metric.key]) || 0)}
            </p>
            <p className="text-xs text-slate-500">
              {last.ymd.slice(5)}{' '}
              {prev ? (
                <AnalyticsDeltaBadge
                  today={Number(last[metric.key]) || 0}
                  yesterday={Number(prev[metric.key]) || 0}
                  compact
                />
              ) : null}
            </p>
          </div>
        ) : null}
      </div>
      <div className="flex gap-1" style={{ height: barAreaPx }}>
        {daily.map((d) => {
          const v = Math.max(0, Number(d[metric.key]) || 0);
          const barPx = v > 0 ? Math.max(compact ? 4 : 6, Math.round((v / max) * barAreaPx)) : 2;
          const isToday = d.ymd === last?.ymd;
          return (
            <div
              key={d.ymd}
              className="flex min-w-0 flex-1 flex-col items-center justify-end gap-0.5"
              title={`${d.ymd}：${formatNum(v)}`}
            >
              <div
                className={cn(
                  'w-full min-w-[4px] rounded-t-md bg-gradient-to-t transition-all',
                  metric.accent,
                  isToday ? 'opacity-100 ring-1 ring-offset-1 ring-slate-400/50' : 'opacity-80'
                )}
                style={{ height: `${barPx}px` }}
              />
              <span
                className={cn(
                  'truncate text-[9px] tabular-nums leading-none sm:text-[10px]',
                  isToday ? 'font-bold text-slate-800' : 'text-slate-400'
                )}
              >
                {d.ymd.slice(8)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
